import useTranslate from '@/lib/hooks/useTranslate';
import { InputTerms } from '../GINDUMAC/InputTerms';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { Input } from '@/components/forms/Input';
import { SuccessCheck } from '@/components/forms/Success/SuccessCheck';
import axios from 'axios';
import { FaSpinner } from 'react-icons/fa';

type FormInputs = {
  company: string;
  'company-position': string;
  'first-name': string;
  'last-name': string;
  email: string;
  password: string;
  terms: boolean;
  message: string;
};

export function SignUpForm({
  isCircularClub,
  onTermsClick,
}: {
  isCircularClub?: boolean;
  onTermsClick?: () => void;
}) {
  const { t } = useTranslate();
  const { register, formState, handleSubmit, setError } = useForm<FormInputs>({
    mode: 'onSubmit',
  });

  async function onSubmit(data: FormInputs) {
    try {
      await axios.post('/api/users/createUser', { ...data, isCircularClub });
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        const apiError = (
          error.response.data as {
            error: { message: string; errors: { message: string } };
          }
        ).error;

        if (apiError.errors.message.includes('PasswordStrengthError')) {
          setError('password', {
            type: 'server',
            message: t(
              // eslint-disable-next-line no-useless-escape
              'mygindumac:Password must be at least 8 characters long and include at least 3 of the following: uppercase letters, lowercase letters, numbers, and special characters'
            ),
          });
        } else {
          setError('root', {
            type: 'server',
            message: apiError.errors.message,
          });
        }
      } else {
        setError('root', {
          type: 'server',
          message: t('mygindumac:An unexpected error occurred'),
        });
      }
    }
  }

  return (
    <form
      className='group relative space-y-4 bg-white px-4 lg:py-8'
      onSubmit={handleSubmit(onSubmit)}
    >
      {process.env.NEXT_PUBLIC_DISABLE_SIGNUP && <SignUpDisabler />}
      {formState.isSubmitSuccessful ? (
        <>
          <SuccessCheck
            message={t('mygindumac:REGISTER_Thank-you-for-signing-up')}
          />
          <h3 className='text-center'>
            {t('mygindumac:REGISTER_Verify-email')}
          </h3>
          <div className='align-center flex w-full flex-col items-center text-2xl text-blue-500 underline'>
            <Link href='/api/auth/login'>{t('products:Login')}</Link>
          </div>
        </>
      ) : (
        <>
          <h3 className='text-center font-secondary text-3xl'>
            {t('mygindumac:Sign Up')}
          </h3>

          {/* Display form-level errors */}
          {formState.errors.root && (
            <div className='text-center text-red-500'>
              {formState.errors.root.message}
            </div>
          )}

          {isCircularClub ? (
            <Input<FormInputs>
              placeholder={t('mygindumac:job-position')}
              name='company-position'
              validation={{
                required: true,
                maxLength: 80,
              }}
              register={register}
              errors={formState.errors}
            />
          ) : (
            <Input<FormInputs>
              placeholder={t('mygindumac:Company Name')}
              name='company'
              validation={{
                required: true,
                maxLength: 80,
              }}
              register={register}
              errors={formState.errors}
            />
          )}
          <div className='grid grid-cols-2 space-x-4'>
            <Input<FormInputs>
              placeholder={t('mygindumac:First Name')}
              name='first-name'
              validation={{
                required: true,
                maxLength: 80,
              }}
              register={register}
              errors={formState.errors}
            />
            <Input<FormInputs>
              placeholder={t('mygindumac:Last Name')}
              name='last-name'
              validation={{
                required: true,
                maxLength: 80,
              }}
              register={register}
              errors={formState.errors}
            />
          </div>
          <Input<FormInputs>
            type='email'
            placeholder={t('mygindumac:Email')}
            name='email'
            validation={{
              required: true,
              maxLength: 180,
            }}
            register={register}
            errors={formState.errors}
          />
          <Input<FormInputs>
            type='password'
            placeholder={t('mygindumac:Password')}
            name='password'
            validation={{
              required: true,
              minLength: 8,
            }}
            register={register}
            errors={formState.errors}
          />
          {/* {isCircularClub && (
            <>
              <h5>{t('mygindumac:message-label')}</h5>
              <ul className='capitalize'>
                <li>(1) {t('products:DETAIL_PAGE_OVERVIEW_MANUFACTURER')}</li>
                <li>(2) {t('products:Model')}</li>
                <li>(3) {t('products:Year of Construction')}</li>
                <li>(4) {t('products:Seller Company')}</li>
                <li>(5) {t('products:Location Address')}</li>
              </ul>
              <Input<FormInputs>
                type='textarea'
                placeholder={t('mygindumac:Message')}
                name='message'
                register={register}
                errors={formState.errors}
              />
            </>
          )} */}
          <InputTerms<FormInputs>
            name='terms'
            register={register}
            errors={formState.errors}
            onClick={onTermsClick}
            hasSingleTerms
          />
          {isCircularClub ? null : (
            <div className='mt-4 space-x-4'>
              <input
                type='checkbox'
                name='newsletter'
                id='newsletter'
                className='h-4 w-4'
              />

              <label htmlFor='newsletter' className='text-sm'>
                <span className='-ml-2 text-sm text-muted-500'>
                  {t('mygindumac:REGISTER_newsletter')}
                </span>
              </label>
            </div>
          )}

          <button
            className='mx-auto mt-4 w-full rounded-md bg-gray-500 p-4 text-xl text-white'
            type='submit'
            disabled={formState.isSubmitting}
          >
            {formState.isSubmitting ? (
              <FaSpinner className='mx-auto animate-spin' />
            ) : (
              t('mygindumac:Submit') + ' '
            )}
          </button>
          {!isCircularClub && (
            <>
              <h4 className='mt-4 text-center text-gray-400'>
                {t('mygindumac:REGISTER_Already-have-an-account') + ' '}
                <Link
                  href='/api/auth/login'
                  className=' text-xl font-medium text-muted-500 hover:underline'
                >
                  {t('mygindumac:Login')}
                </Link>
              </h4>
            </>
          )}
        </>
      )}
    </form>
  );
}

function SignUpDisabler() {
  const { t } = useTranslate();
  return (
    <div className='absolute left-0 top-0  hidden group-hover:block group-hover:h-full group-hover:w-full group-hover:bg-gray-600'>
      <h1 className='mt-24 px-4 text-center text-white'>
        Sign Up is disabled for now, please contact us for beta preview access
        or go to{' '}
        <Link href='/api/auth/login' className='lg:text-xl '>
          <h1 className='text-blue-500 underline'>{t('mygindumac:Login')}</h1>
        </Link>
      </h1>
    </div>
  );
}
